import { api } from "../../../api/apiSlice";

// Quotation Prototype (branch-wise reusable line-item templates) + Quotation
// (submit -> approve -> print/email/WhatsApp), against TexasAlarms.Api's
// src/Quotations module - listed under the Sales Monitoring nav section per
// direct instruction but its own independent API/DB, not an extension of
// Sales Enquiry/Quotation. Follows the exact query/mutation/tag convention
// established by salesApi.js/pettyCashApi.js.
export const quotationsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // ---- Prototype ----
    listQuotationPrototypes: builder.query({
      query: (params) => ({ url: "/quotations/prototypes", params }),
      providesTags: (result) =>
        result
          ? [...result.map((p) => ({ type: "QuotationPrototype", id: p.recordGuid })), { type: "QuotationPrototype", id: "LIST" }]
          : [{ type: "QuotationPrototype", id: "LIST" }],
    }),
    getQuotationPrototype: builder.query({
      query: (recordGuid) => ({ url: `/quotations/prototypes/${recordGuid}` }),
      providesTags: (result, error, recordGuid) => [{ type: "QuotationPrototype", id: recordGuid }],
    }),
    createQuotationPrototype: builder.mutation({
      query: (body) => ({ url: "/quotations/prototypes", method: "POST", data: body }),
      invalidatesTags: [{ type: "QuotationPrototype", id: "LIST" }],
    }),
    updateQuotationPrototype: builder.mutation({
      query: ({ recordGuid, ...body }) => ({ url: `/quotations/prototypes/${recordGuid}`, method: "PUT", data: body }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "QuotationPrototype", id: recordGuid },
        { type: "QuotationPrototype", id: "LIST" },
      ],
    }),
    setQuotationPrototypeActive: builder.mutation({
      query: ({ recordGuid, isActive }) => ({ url: `/quotations/prototypes/${recordGuid}/active`, method: "PUT", data: { isActive } }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "QuotationPrototype", id: recordGuid },
        { type: "QuotationPrototype", id: "LIST" },
      ],
    }),

    // ---- Quotation ----
    listQuotations: builder.query({
      query: (params) => ({ url: "/quotations", params }),
      providesTags: (result) =>
        result?.items
          ? [...result.items.map((q) => ({ type: "Quotation", id: q.recordGuid })), { type: "Quotation", id: "LIST" }]
          : [{ type: "Quotation", id: "LIST" }],
    }),
    getQuotation: builder.query({
      query: (recordGuid) => ({ url: `/quotations/${recordGuid}` }),
      providesTags: (result, error, recordGuid) => [{ type: "Quotation", id: recordGuid }],
    }),
    createQuotation: builder.mutation({
      query: (body) => ({ url: "/quotations", method: "POST", data: body }),
      invalidatesTags: [{ type: "Quotation", id: "LIST" }],
    }),
    updateQuotation: builder.mutation({
      query: ({ recordGuid, ...body }) => ({ url: `/quotations/${recordGuid}`, method: "PUT", data: body }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "Quotation", id: recordGuid },
        { type: "Quotation", id: "LIST" },
      ],
    }),
    approveQuotation: builder.mutation({
      query: ({ recordGuid, ...body }) => ({ url: `/quotations/${recordGuid}/approve`, method: "POST", data: body }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "Quotation", id: recordGuid },
        { type: "Quotation", id: "LIST" },
      ],
    }),
    rejectQuotation: builder.mutation({
      query: ({ recordGuid, rejectionReason }) => ({ url: `/quotations/${recordGuid}/reject`, method: "POST", data: { rejectionReason } }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "Quotation", id: recordGuid },
        { type: "Quotation", id: "LIST" },
      ],
    }),
    cancelQuotation: builder.mutation({
      query: ({ recordGuid, cancelReason }) => ({ url: `/quotations/${recordGuid}/cancel`, method: "POST", data: { cancelReason } }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "Quotation", id: recordGuid },
        { type: "Quotation", id: "LIST" },
      ],
    }),
    // Batch164 cross-link: link a quotation to a sales enquiry/order from the
    // sales side (salesEnquiryRecordGuid null = unlink).
    setQuotationSalesLink: builder.mutation({
      query: ({ recordGuid, salesEnquiryRecordGuid }) => ({
        url: `/quotations/${recordGuid}/sales-link`, method: "PUT", data: { salesEnquiryRecordGuid },
      }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "Quotation", id: recordGuid },
        { type: "Quotation", id: "LIST" },
      ],
    }),

    // ---- Delivery ----
    // `brochures` (2026-09-09) ride along on the same email and are NOT stored
    // anywhere - so they exist only for this one send, and a later Resend
    // carries the quotation alone. The field name must stay `brochures` to
    // bind to the controller's IFormFileCollection.
    uploadQuotationDocument: builder.mutation({
      query: ({ recordGuid, file, brochures = [] }) => {
        const formData = new FormData();
        formData.append("file", file, "quotation.pdf");
        brochures.forEach((b) => formData.append("brochures", b, b.name));
        return { url: `/quotations/${recordGuid}/document`, method: "POST", data: formData };
      },
      invalidatesTags: (result, error, { recordGuid }) => [{ type: "Quotation", id: recordGuid }],
    }),
    resendQuotationDocument: builder.mutation({
      query: (recordGuid) => ({ url: `/quotations/${recordGuid}/document/resend`, method: "POST" }),
      invalidatesTags: (result, error, recordGuid) => [{ type: "Quotation", id: recordGuid }],
    }),
    // Authenticated PDF - fetched as a Blob (axiosBaseQuery's responseType passthrough).
    getQuotationDocumentPdf: builder.query({
      query: (recordGuid) => ({ url: `/quotations/${recordGuid}/document.pdf`, responseType: "blob" }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useListQuotationPrototypesQuery,
  useGetQuotationPrototypeQuery,
  useLazyGetQuotationPrototypeQuery,
  useCreateQuotationPrototypeMutation,
  useUpdateQuotationPrototypeMutation,
  useSetQuotationPrototypeActiveMutation,
  useListQuotationsQuery,
  useGetQuotationQuery,
  useLazyGetQuotationQuery,
  useCreateQuotationMutation,
  useUpdateQuotationMutation,
  useApproveQuotationMutation,
  useRejectQuotationMutation,
  useCancelQuotationMutation,
  useSetQuotationSalesLinkMutation,
  useUploadQuotationDocumentMutation,
  useResendQuotationDocumentMutation,
  useLazyGetQuotationDocumentPdfQuery,
} = quotationsApi;
